import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import ArialNormal from "../../../../utils/Customfonts/ARIAL.TTF";
import ArialBold from "../../../../utils/Customfonts/ARIALBD.TTF";
import ArialItalic from "../../../../utils/Customfonts/ARIALI.TTF";
import ArialBoldItalic from "../../../../utils/Customfonts/ARIALBI.TTF";

const DYC_LOGO = "/img/dycgpl-logo.webp";

export const customFontFamily = {
  bold: "bold",
  normal: "normal",
  boldFont: "Arial",
  normalFont: "Arial",
  italic: "italic",
  italicFont: "Arial",
};

// -----------------------------------------------------------------------------
// GLOBAL CONFIG & STYLES (2-Page Balanced Layout with Larger Readable Font)
// -----------------------------------------------------------------------------

export const config = {
  borderMargin: 16,
  contentMargin: 24,

  get contentWidth() {
    return 595.28 - this.contentMargin * 2;
  },

  fontSize: 9.2,
  fontSizeSm: 8.5,
  lineHeight: 13.2,
  paragraphGap: 8.0,
};

const getBaseStyles = () => ({
  fontSize: config.fontSizeSm,
  cellPadding: { top: 5, bottom: 5, left: 5, right: 5 },
  lineColor: [60, 60, 60],
  lineWidth: 0.5,
  textColor: [0, 0, 0],
  fillColor: false,
  font: customFontFamily.normalFont,
  fontStyle: customFontFamily.normal,
  halign: "left",
  valign: "middle",
});

const getHeaderStyles = () => ({
  ...getBaseStyles(),
  font: customFontFamily.boldFont,
  fontStyle: customFontFamily.bold,
  fillColor: [240, 243, 248],
  textColor: [0, 0, 0],
  halign: "left",
  valign: "middle",
});

// -----------------------------------------------------------------------------
// FONT LOADING
// -----------------------------------------------------------------------------

const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const loadAndAddFont = async (pdfDoc, fontUrl, fontNameTTF, fontName, fontStyle) => {
  try {
    const response = await fetch(fontUrl);
    const buffer = await response.arrayBuffer();
    const base64 = arrayBufferToBase64(buffer);

    pdfDoc.addFileToVFS(`${fontNameTTF}.ttf`, base64);
    pdfDoc.addFont(`${fontNameTTF}.ttf`, fontName, fontStyle);
  } catch (error) {
    console.error(`Font loading failed: ${fontName}`, error);
  }
};

// -----------------------------------------------------------------------------
// PAGE DECORATORS (Outer Border, Header Line, Logo, Footers on EVERY page)
// -----------------------------------------------------------------------------

const addPageDecorators = (doc, totalPages, data = {}) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);

    // 1. OUTER BORDER
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.8);
    doc.rect(
      config.borderMargin,
      config.borderMargin,
      pageWidth - config.borderMargin * 2,
      pageHeight - config.borderMargin * 2
    );

    // 2. FORM HEADER TITLE
    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.setFontSize(13);
    doc.setTextColor(20, 20, 20);
    doc.text(
      "Independence, Impartiality, Integrity and Confidentiality Declaration",
      pageWidth / 2,
      config.borderMargin + 25,
      { align: "center" }
    );

    // 3. LOGO (Top Right)
    try {
      const logoSize = 34;
      const logoX = pageWidth - config.borderMargin - logoSize - 12;
      const logoY = config.borderMargin + 6;

      try {
        doc.addImage(DYC_LOGO, "WEBP", logoX, logoY, logoSize, logoSize);
      } catch (_) {
        try {
          doc.addImage(DYC_LOGO, "PNG", logoX, logoY, logoSize, logoSize);
        } catch (__) {
          doc.addImage(DYC_LOGO, logoX, logoY, logoSize, logoSize);
        }
      }
    } catch (_) {
      // Logo optional
    }

    // 4. HEADER DIVIDER LINE
    doc.setLineWidth(0.8);
    doc.line(
      config.borderMargin,
      config.borderMargin + 44,
      pageWidth - config.borderMargin,
      config.borderMargin + 44
    );

    // 5. FOOTER
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
    doc.setFontSize(7.5);
    doc.setTextColor(40, 40, 40);

    const docCode = `${data.docCode || "DYC/FMT-MR/13"} Rev.: ${data.revNo || "01"}, Eff.Dated -${data.effDate || "30/11/2024"}`;
    const footerY1 = pageHeight - config.borderMargin - 15;
    const footerY2 = pageHeight - config.borderMargin - 5;

    // Line 1 Left
    doc.text(docCode, config.borderMargin + 8, footerY1);

    // Line 2 Left
    doc.text(
      "Company General Use. All Rights Reserved by DYC Global Pvt Ltd.",
      config.borderMargin + 8,
      footerY2
    );

    // Right Page Number
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
    doc.text(`Page ${page} of ${totalPages}`, pageWidth - config.borderMargin - 55, footerY1);
  }
};

// -----------------------------------------------------------------------------
// TEXT DRAWING HELPER (With precise wrapping & line spacing)
// -----------------------------------------------------------------------------

const drawParagraph = (doc, text, x, y, width, fontStyle = "normal", isBold = false) => {
  if (fontStyle === "italic" && isBold) {
    doc.setFont(customFontFamily.boldFont, "bolditalic");
  } else if (fontStyle === "italic") {
    doc.setFont(customFontFamily.italicFont, customFontFamily.italic);
  } else if (isBold) {
    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  } else {
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  }

  doc.setFontSize(config.fontSize);
  doc.setTextColor(20, 20, 20);

  const lines = doc.splitTextToSize(text, width);
  doc.text(lines, x, y, { maxWidth: width, align: "justify" });

  return y + lines.length * config.lineHeight + config.paragraphGap;
};

// -----------------------------------------------------------------------------
// MAIN GENERATOR EXPORT
// -----------------------------------------------------------------------------

export const generateIntegrityDeclarationPDF = async (data = {}) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
    compress: true,
  });

  await Promise.all([
    loadAndAddFont(doc, ArialNormal, "Arial-Normal", customFontFamily.normalFont, customFontFamily.normal),
    loadAndAddFont(doc, ArialBold, "Arial-Bold", customFontFamily.boldFont, customFontFamily.bold),
    loadAndAddFont(doc, ArialItalic, "Arial-Italic", customFontFamily.italicFont, customFontFamily.italic),
    loadAndAddFont(doc, ArialBoldItalic, "Arial-BoldItalic", customFontFamily.normalFont, "bolditalic"),
  ]);

  const leftX = config.contentMargin + 4;
  const contentW = config.contentWidth - 8;

  // ===========================================================================
  // PAGE 1: PLEDGE & DECLARATION PARAGRAPHS (1 to 11)
  // ===========================================================================
  let currentY = config.borderMargin + 62;

  // Para 1: Name pledge
  const empName = data.employeeName ? ` ${data.employeeName} ` : " ______________________________ ";
  const p1 = `I, ${empName}, solemnly declare and affirm my unwavering commitment to upholding the highest standards of professionalism and ethical conduct during my tenure at DYC Global Private Limited. In accordance with the company's principles and policies, I pledge to execute all my responsibilities with utmost fairness, independence, impartiality, integrity, and confidentiality.`;
  currentY = drawParagraph(doc, p1, leftX, currentY, contentW, "normal");

  // Para 2: Associations
  const p2 = `I confirm that I have no associations or affiliations with the design, manufacture, supply, installation, purchase, ownership, usage, or maintenance of the items under inspection ensuring complete independence in my judgment and actions. Furthermore, I commit to maintaining absolute impartiality in my inspection activities, refraining from engagements or relationships that could compromise the integrity of my decisions. Also, I don't provide any consultancy or advisory service to this parties.`;
  currentY = drawParagraph(doc, p2, leftX, currentY, contentW, "normal");

  // Para 3: 2-year abstinence & gifts
  const p3 = `I acknowledge that, before commencing any inspection, I have abstained from any work, services, or relationships with the client/manufacturer for a minimum period of two years to ensure unbiased assessments. I pledge to refuse any conditional Favors, gifts, or gratuities, except for commonly accepted unconditional token gifts like sweets or dairy.`;
  currentY = drawParagraph(doc, p3, leftX, currentY, contentW, "normal");

  // Para 4: Fair inspection
  const p4 = `I hereby affirm my dedication to providing inspection services to all parties equally and without any undue financial or discriminatory conditions. I assure comprehensive inspections aligned with specified scopes, codes, standards, and relevant documents, adhering strictly to DYC Global Quality Manuals, Work Instructions, and Procedures.`;
  currentY = drawParagraph(doc, p4, leftX, currentY, contentW, "normal");

  // Para 5: Timely reports
  const p5 = `Moreover, I guarantee the timely provision of reports within agreed-upon formats and timeframes, ensuring detailed communication of any anomalies observed during inspections to both my office and the client. I am committed to continuous learning, staying updated on procedures, contractual obligations, and technical advancements, ensuring the renewal of my professional qualifications promptly.`;
  currentY = drawParagraph(doc, p5, leftX, currentY, contentW, "normal");

  // Para 6: Responsibility & liability (Italic)
  const p6 = `I acknowledge my responsibility and liability for conducting accurate inspections and ensuring the correctness of my assessments in line with professional standards.`;
  currentY = drawParagraph(doc, p6, leftX, currentY, contentW, "italic", false);

  // Para 7: Conflict of interest (Italic Bold)
  const p7 = `I hereby affirm that I will not accept assignments to conduct inspections at installations where any potential conflicts of interest may exist. This includes situations where inspectors from different regions are involved, which could compromise the impartiality or objectivity of the inspection process. I pledge to uphold the principles of independence, fairness, and integrity in all inspection activities, ensuring that the credibility and trustworthiness of the inspection results are maintained at the highest standards`;
  currentY = drawParagraph(doc, p7, leftX, currentY, contentW, "italic", true);

  // Para 8: Competencies
  const p8 = `In adherence to my documented competencies, I pledge to undertake inspection tasks only within my skill set or competence, ensuring thorough and comprehensive assessments. In the event of conflicts of interest or non-compliance concerns, I vow to immediately report to my MD-Technical/Operational Manager/Technical Manager and, if necessary, withdraw from the inspection, disclosing any external engagements.`;
  currentY = drawParagraph(doc, p8, leftX, currentY, contentW, "normal");

  // Para 9: Confidentiality
  const p9 = `I acknowledge and uphold complete confidentiality towards DYC Global, its clients, and vendors, maintaining the privacy of project details, gathered information, and records obtained during inspections. This confidentiality commitment remains in effect beyond the termination of individual contracts or employment with DYC Global. However, it excludes information already in the public domain, obtained independently, or pre-existing within DYC Global's possession before disclosure during service. Breaches of this obligation may hold DYC Global accountable to clients, particularly for intentional or grossly negligent violations, within specified liability constraints.`;
  currentY = drawParagraph(doc, p9, leftX, currentY, contentW, "normal");

  // Para 10: Proprietary data
  const p10 = `Employees, on the other hand, must maintain the confidentiality of the Company's proprietary data, refraining from disclosing or utilizing such information both during their tenure and post-employment. This includes refraining from contributing to publications, whether compensated or not. Compliance with the Company's secrecy agreement, adherence to security protocols, including those guided by the Official Secrets Act, and vigilance in IT and data security, is mandatory.`;
  currentY = drawParagraph(doc, p10, leftX, currentY, contentW, "normal");

  // Para 11: Code of Conduct Reaffirmation (Italic Bold)
  const p11 = `I reaffirm my commitment to adhere to the full code of conduct as described in Appendix-I of DYC Global's procedures DYC /QP-IB/01 REV.00, ensuring compliance with all ethical and procedural standards.`;
  currentY = drawParagraph(doc, p11, leftX, currentY, contentW, "italic", true);

  // ===========================================================================
  // PAGE 2: FINAL PARAGRAPH, EMPLOYMENT HISTORY TABLE & SIGNATURES
  // ===========================================================================
  doc.addPage();
  currentY = config.borderMargin + 64;

  // Final Paragraph
  const p12 = `Lastly, I acknowledge my responsibility for conducting accurate inspections and affirm my understanding and compliance with DYC Global's code of conduct and procedures. I pledge to uphold the principles of fairness, independence, impartiality, and integrity throughout my tenure at DYC Global Private Limited.`;
  currentY = drawParagraph(doc, p12, leftX, currentY, contentW, "normal");

  currentY += 8;

  // Subtitle: Employment History Header
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(9.5);
  doc.setTextColor(20, 20, 20);
  doc.text("Below, I provide details of my employment history for the preceding two years:", leftX, currentY);
  currentY += 14;

  // Employment History Table (Dynamic all rows)
  const rawHist = Array.isArray(data.employmentHistory) && data.employmentHistory.length > 0
    ? data.employmentHistory
    : [
        { employerName: "", designation: "", serviceDuration: "", employmentType: "Permanent" },
        { employerName: "", designation: "", serviceDuration: "", employmentType: "Permanent" },
      ];

  const tableRows = rawHist.map((row, index) => [
    { content: String(index + 1), styles: { halign: "center", font: customFontFamily.normalFont, fontStyle: customFontFamily.normal } },
    { content: row.employerName || "", styles: { font: customFontFamily.normalFont, fontStyle: customFontFamily.normal } },
    { content: row.designation || "", styles: { font: customFontFamily.normalFont, fontStyle: customFontFamily.normal } },
    { content: row.serviceDuration || "", styles: { font: customFontFamily.normalFont, fontStyle: customFontFamily.normal } },
    { content: row.employmentType || "Permanent", styles: { font: customFontFamily.normalFont, fontStyle: customFontFamily.normal } },
  ]);

  autoTable(doc, {
    startY: currentY,
    head: [
      [
        { content: "Sr.No.", styles: { halign: "center" } },
        { content: "Employer Name" },
        { content: "Designation" },
        { content: "Service Duration" },
        { content: "Type of Employment Contract / Permanent / Part Time" },
      ],
    ],
    body: tableRows,
    theme: "grid",
    styles: { ...getBaseStyles(), fontSize: config.fontSizeSm, minCellHeight: 22 },
    headStyles: getHeaderStyles(),
    columnStyles: {
      0: { cellWidth: contentW * 0.08, halign: "center" },
      1: { cellWidth: contentW * 0.28 },
      2: { cellWidth: contentW * 0.22 },
      3: { cellWidth: contentW * 0.20 },
      4: { cellWidth: contentW * 0.22 },
    },
    margin: { left: leftX, right: leftX },
  });

  currentY = doc.lastAutoTable.finalY + 40;

  // Signatures Section (Matching Physical Reference on Page 2)
  const pageWidth = doc.internal.pageSize.getWidth();
  const rightX = pageWidth - config.borderMargin - 200;

  // Left side: Place & Date
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(9.5);
  doc.text(`Place- `, leftX, currentY);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(`${data.place || ""}`, leftX + 38, currentY);

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.text(`Date- `, leftX, currentY + 26);
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(`${data.declarationDate || ""}`, leftX + 34, currentY + 26);

  // Right side: Sign & Name & Signature of Employee
  if (data.signatureSign) {
    doc.setFont(customFontFamily.italicFont, customFontFamily.italic);
    doc.setFontSize(12);
    doc.setTextColor(0, 30, 160);
    doc.text(`${data.signatureSign}`, rightX + 15, currentY - 4);
    doc.setTextColor(20, 20, 20);
  }

  if (data.signatureName || data.employeeName) {
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
    doc.setFontSize(9.5);
    doc.text(`${data.signatureName || data.employeeName}`, rightX + 15, currentY + 14);
  }

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(9.5);
  doc.text("Name & Signature of Employee", rightX, currentY + 30);

  // ===========================================================================
  // PAGE DECORATORS (Outer Border, Header, Footers on all pages)
  // ===========================================================================
  const totalPages = doc.internal.getNumberOfPages();
  addPageDecorators(doc, totalPages, data);

  return doc;
};

export default generateIntegrityDeclarationPDF;
